import { FC } from "react";

/**
 * Post-competition landing view, shown once the api reports a finalization tx
 * (the L1 transaction that closed the head and returned everyone's ADA). The
 * tx link is the proof of refund; the leaderboard stays reachable for final
 * standings. Reachable at /thanks before finalization too, so every claim
 * that depends on the tx falls back gracefully when it is null.
 */

const SPRINKLES = [
  { left: "6%", top: "16%", color: "var(--color-sr-bubblegum)", delay: "0s", rotate: "12deg" },
  { left: "14%", top: "70%", color: "var(--color-sr-lemon)", delay: "0.6s", rotate: "-24deg" },
  { left: "24%", top: "32%", color: "var(--color-sr-sky)", delay: "1.4s", rotate: "40deg" },
  { left: "36%", top: "84%", color: "var(--color-sr-mint)", delay: "0.9s", rotate: "-8deg" },
  { left: "64%", top: "12%", color: "var(--color-sr-tangerine)", delay: "0.3s", rotate: "28deg" },
  { left: "74%", top: "66%", color: "var(--color-sr-pink-bright)", delay: "1.1s", rotate: "-32deg" },
  { left: "86%", top: "26%", color: "var(--color-sr-lemon)", delay: "1.8s", rotate: "8deg" },
  { left: "92%", top: "76%", color: "var(--color-sr-sky)", delay: "0.4s", rotate: "-16deg" },
] as const;

export const CompetitionOverView: FC<{
  finalizationTx: string | null;
  onViewLeaderboard: () => void;
  onViewDeposits: () => void;
}> = ({ finalizationTx, onViewLeaderboard, onViewDeposits }) => {
  const explorerHref = finalizationTx
    ? `https://${window.__APP_CONFIG.env === "mainnet" ? "" : "preview."}cexplorer.io/tx/${finalizationTx}`
    : null;

  return (
    <div className="relative flex w-full max-w-lg flex-1 flex-col items-center justify-center gap-6 py-10 text-center">
      {SPRINKLES.map((s, i) => (
        <span
          key={i}
          aria-hidden
          className="absolute h-5 w-2 animate-pulse rounded-full"
          style={{
            left: s.left,
            top: s.top,
            backgroundColor: s.color,
            transform: `rotate(${s.rotate})`,
            animationDelay: s.delay,
            animationDuration: "3s",
            opacity: 0.4,
          }}
        />
      ))}

      <span aria-hidden className="text-6xl sm:text-7xl">
        🍭
      </span>

      <h1
        className="text-3xl font-semibold text-sr-lemon sm:text-4xl"
        style={{ fontFamily: "var(--font-display)" }}
      >
        That&apos;s a wrap!
      </h1>

      <p className="text-base text-sr-oat-milk sm:text-lg">
        The Sugar Rush trading competition ended September 6th at 20:00 UTC.
        <br />
        Thanks for competing!
      </p>

      <div className="flex w-full max-w-sm flex-col gap-3">
        <button
          onClick={onViewLeaderboard}
          className="sr-focus-ring rounded-full bg-sr-lemon-muted px-5 py-2.5 text-sm font-medium text-sr-espresso transition-colors hover:bg-sr-lemon"
        >
          See the final standings
        </button>
        {explorerHref ? (
          <a
            href={explorerHref}
            target="_blank"
            rel="noreferrer"
            className="sr-focus-ring rounded-full border border-sr-border-subtle px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/5"
          >
            Your ADA was returned in this transaction →
          </a>
        ) : (
          <button
            onClick={onViewDeposits}
            className="sr-focus-ring rounded-full border border-sr-border-subtle px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/5"
          >
            Withdraw your ADA
          </button>
        )}
      </div>

      <div className="w-full max-w-sm rounded-xl border border-sr-border-subtle bg-sr-surface p-5 text-left">
        <ul className="flex flex-col gap-2.5 text-xs leading-relaxed text-sr-neutral-300">
          <li>
            Winners are announced after the integrity review of provisional
            standings.
          </li>
          <li>
            {finalizationTx
              ? "All deposited ADA was returned on Cardano in the finalization transaction linked above."
              : "Your deposited ADA remains yours and stays withdrawable from the Deposits tab."}
          </li>
          <li>
            Coverage-prize entries are open until September 13th — submit yours
            to{" "}
            <a
              href="mailto:sugar-rush@sundae.fi"
              className="text-sr-pink-light underline decoration-sr-pink-muted underline-offset-2 hover:text-sr-lemon-muted"
            >
              sugar-rush@sundae.fi
            </a>
            .
          </li>
          <li>
            Results and what&apos;s next:{" "}
            <a
              href="https://x.com/SugarRushDEX"
              target="_blank"
              rel="noreferrer"
              className="text-sr-pink-light underline decoration-sr-pink-muted underline-offset-2 hover:text-sr-lemon-muted"
            >
              @SugarRushDEX
            </a>
            .
          </li>
        </ul>
      </div>
    </div>
  );
};
